/**
 * SkillBadges — what a folded skill rests on, and until when.
 *
 * This replaces AttestationBadges, whose "source" badge named ONE origin
 * (self_declared, peer_endorsement, ai_session_inference, certification). A
 * skill has no single source: `basis` is an array naming every KIND of record
 * behind it, and a skill held on a declaration alone is a different claim from
 * one a colleague endorsed and the institution qualified. Collapsing that back
 * to one word would throw away the distinction the fold exists to make.
 *
 * Three signals, in the order a nurse reads them:
 *
 *  - Coding (prominent, citation tint): the clinical coding is the
 *    search-strength signal — a coded skill is discoverable by a colleague
 *    looking for "wer kennt sich mit X aus".
 *  - Basis (quiet): one chip per kind of record. Never colour-only; each
 *    carries its own text.
 *  - Lapse (quiet): the last day a qualification behind the skill covers.
 *    Rendered only when `period.end` is set — a null `period`, or a null `end`
 *    inside it, means no qualification supports the skill or none expires,
 *    which is a claim and not a missing value, so it earns no placeholder. The
 *    period is half-open, so the day shown is the day BEFORE `end`.
 *
 * DESIGN.md: pill radius, no shadow, flat hairline, tone carried by text.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { HeldSkill, RecordKind, SkillCoding } from '@frontend/api/endpoints/skills'
import { periodLastDay } from '@frontend/utils/dates'

function codingLabel(coding: SkillCoding): string {
  return coding.display || `${coding.system}:${coding.code}`
}

export function SkillBadges({ skill }: { skill: HeldSkill }) {
  const { t } = useTranslation()
  const { coding, basis, period } = skill
  // `period` is a REQUIRED key that is nullable, never an optional one:
  // parseResponse refuses a body without it, so an absent period cannot reach
  // this component and `?.` would only hide the day it does.
  const lastCoveredDay = period === null || period.end === null ? null : periodLastDay(period.end)

  // Memoised on `t` so the map is not rebuilt on every render; it changes only
  // when the active locale changes.
  const BASIS_LABELS = useMemo<Record<RecordKind, string>>(
    () => ({
      declaration: t('skills.basis.declaration'),
      endorsement: t('skills.basis.endorsement'),
      qualification: t('skills.basis.qualification'),
      attempt: t('skills.basis.attempt'),
      proposal: t('skills.basis.proposal'),
      target: t('skills.basis.target'),
    }),
    [t],
  )

  return (
    <div className="flex flex-wrap items-center gap-2">
      {coding ? (
        <span
          className="inline-flex items-center rounded-full bg-[var(--color-citation-50)] px-2.5 py-0.5 text-[12px] font-semibold text-[var(--color-citation-700)]"
          title={`${coding.system}:${coding.code}`}
        >
          {codingLabel(coding)}
        </span>
      ) : null}

      {basis.map((kind) => (
        <span key={kind} className="text-[12px] text-[var(--color-surface-mute)]">
          {BASIS_LABELS[kind]}
        </span>
      ))}

      {lastCoveredDay ? (
        <span className="text-[12px] text-[var(--color-surface-mute)]">
          {t('skills.badges.validUntil', { date: lastCoveredDay })}
        </span>
      ) : null}
    </div>
  )
}

export default SkillBadges
